// Accounting › Banking (Blueprint's; pages/ux-full accounting/banking), its own module out of Books.tsx, on
// ui-web's ListPage (patterns/README.md, BL-189). The title block's figures are the account's balances
// and how many lines wait. The list is To review: each bank line from the feed or a statement, matched to
// what the books already hold or categorised to an account (BankingReview.tsx). Beside it, the
// reconciliation against the statement (finished when the difference is $0.00; the last finished one
// reopened with a reason), then the cash and cheques to deposit (a Checkbox a payment) and bringing in a
// statement (a FileField, then which column is which). Under it all, what's matched, each with Unmatch
// (a DataTable). The money confirms are ConfirmDialogs. Riverstone keeps books/Banking.tsx and
// BankingEntry.tsx; the statement's helpers are statement-model.ts'. banking-page.css lays out the parts,
// tokens only.
import {
  Button,
  Checkbox,
  ConfirmDialog,
  DataTable,
  FileField,
  Figures,
  KeyValue,
  Link,
  Segmented,
  SelectField,
  TextField,
  type DataColumn,
} from '@flowology/ui-web/fl';
import { ListPage, Section, type ListStatus } from '@flowology/ui-web/patterns';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { BankingReview } from './BankingReview.tsx';
import { CLOSE_PERMS, reopenOutcome } from './corrections-model.ts';
import { dayIn, dayText, money, parseMoney, readUntil, todayIn } from './model.ts';
import { BOOKS_PARTS } from './parts.ts';
import type { BooksPart } from './props.ts';
import {
  csvColumns,
  csvHeader,
  csvReady,
  guessColumns,
  statementKind,
  type Columns,
} from './statement-model.ts';
import type { Account, BankAccount, BankLine, Reconciliation } from './types.ts';
import { errorText, useAct, useQuery } from './ui.tsx';
import './banking-page.css';

const PART = BOOKS_PARTS.find((p) => p.part === 'banking')!;
const CONTROL = new Set(['accounts_receivable', 'accounts_payable', 'customer_credits']);
const isControl = (a: Account) => (a.roles ?? []).some((r) => CONTROL.has(r.role));
/** A region's state from what's read: its rows (none is empty), why it didn't load, or still loading. */
const stateOf = (rows: readonly unknown[] | null | undefined, error: string | null): ListStatus =>
  rows ? (rows.length ? 'ready' : 'empty') : error ? 'failed' : 'loading';

// ── the reconciliation, beside the list ──

function Reconcile({
  bank,
  toast,
  mayReopen,
  onChanged,
}: {
  bank: BankAccount;
  toast: (m: string) => void;
  /** accounting.close: reopen the last finished reconciliation. */
  mayReopen: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const act = useAct(toast, async () => {
    await onChanged();
  });
  const [end, setEnd] = useState(todayIn());
  const [closing, setClosing] = useState('');
  // The account's first reconciliation starts from the statement's own opening (QA-801).
  const first = !bank.reconciledThrough;
  const [opening, setOpening] = useState('');
  const [rec, setRec] = useState<Reconciliation | null>(null);
  const [reopening, setReopening] = useState(false);
  /** Changing the statement of the one in progress (its end date or closing balance). */
  const [changing, setChanging] = useState(false);
  // The one in progress is the account's until it's finished: carry on with it (QA-800).
  const openId = bank.openReconciliation?.id ?? null;
  useEffect(() => {
    if (!openId) return;
    let live = true;
    void client
      .call<Reconciliation>('bank.reconciliationStatus', { reconciliationId: openId })
      .then((r) => {
        if (live) setRec((now) => now ?? r);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [openId]);
  const start = async () => {
    const cents = parseMoney(closing);
    if (cents === null) return toast('Type the statement’s closing balance, like 12,345.67.');
    const openingCents = first && opening.trim() ? parseMoney(opening) : undefined;
    if (openingCents === null) return toast('Type the statement’s opening balance, like 12,345.67.');
    const r = await act.run<Reconciliation>('bank.startReconciliation', {
      bankAccountId: bank.id,
      statementEndDate: end,
      closingCents: cents,
      ...(openingCents === undefined ? {} : { openingCents }),
    });
    if (r) {
      setRec(r);
      setChanging(false);
    }
  };
  const change = () => {
    if (!rec) return;
    setEnd(rec.statementEndDate.slice(0, 10));
    setClosing((rec.closingCents / 100).toFixed(2));
    if (first) setOpening((rec.openingCents / 100).toFixed(2));
    setChanging(true);
  };
  const refresh = async () => {
    if (rec)
      setRec(await client.call<Reconciliation>('bank.reconciliationStatus', { reconciliationId: rec.id }));
  };
  const last = bank.lastReconciliation;
  const statementTo = rec ? dayText(rec.statementEndDate.slice(0, 10)) : '';
  return (
    <div className="bp-bank-rec">
      <p className="bp-bank-say">
        {bank.reconciledThrough
          ? `Reconciled through ${dayText(bank.reconciledThrough)}.`
          : 'Not reconciled yet.'}
      </p>
      {!rec && mayReopen && last && (
        <div className="bp-bank-acts">
          <Button onPress={() => setReopening(true)}>Reopen the last reconciliation</Button>
        </div>
      )}
      {rec && !changing && rec.status === 'in_progress' && (
        <p className="bp-bank-say">{`In progress, against the statement to ${statementTo}.`}</p>
      )}
      {rec && rec.status === 'finished' && (
        <p className="bp-bank-say">{`Finished, against the statement to ${statementTo}.`}</p>
      )}
      {!rec || changing ? (
        <div className="bp-bank-form">
          <TextField type="date" label="Statement ends" value={end} onChange={setEnd} />
          {first && (
            <TextField
              label="Statement opening"
              inputMode="decimal"
              value={opening}
              onChange={setOpening}
              placeholder={(bank.reconciledBalanceCents / 100).toFixed(2)}
            />
          )}
          <TextField
            label="Closing balance"
            inputMode="decimal"
            value={closing}
            onChange={setClosing}
            placeholder="0.00"
          />
          <div className="bp-bank-go">
            <Button variant="primary" isDisabled={act.busy} onPress={() => void start()}>
              {changing ? 'Change' : 'Start'}
            </Button>
          </div>
          {changing && (
            <div className="bp-bank-go">
              <Button onPress={() => setChanging(false)}>Keep it as it was</Button>
            </div>
          )}
        </div>
      ) : (
        <>
          <KeyValue
            label="The reconciliation"
            items={[
              { label: 'Statement opening', value: money(rec.openingCents) },
              { label: 'Statement closing', value: money(rec.closingCents) },
              { label: `Cleared in Flowology (${rec.clearedCount})`, value: money(rec.clearedCents) },
              { label: 'Difference', value: money(rec.differenceCents), bad: rec.differenceCents !== 0 },
            ]}
          />
          <div className="bp-bank-acts">
            <Button onPress={() => void refresh()}>Recheck</Button>
            {rec.status === 'in_progress' && <Button onPress={change}>Change the statement</Button>}
            <Button
              variant="primary"
              isDisabled={act.busy || rec.differenceCents !== 0 || rec.status === 'finished'}
              onPress={() =>
                void (async () => {
                  const r = await act.run<Reconciliation>(
                    'bank.finishReconciliation',
                    { reconciliationId: rec.id },
                    'Reconciled.',
                  );
                  if (r) setRec(r);
                })()
              }
            >
              {rec.differenceCents === 0 ? 'Finish' : 'Finish (difference must be $0.00)'}
            </Button>
          </div>
        </>
      )}
      {last && (
        <ConfirmDialog
          isOpen={reopening}
          onClose={() => setReopening(false)}
          title={`Reopen the reconciliation through ${dayText(last.through)}?`}
          confirmLabel="Reopen it"
          cancelLabel="Cancel"
          reason={{
            label: 'Why',
            placeholder: 'The fee was matched to the wrong deposit',
            missing: 'Say why it’s reopened.',
            maxLength: 300,
          }}
          onConfirm={async (reason) => {
            try {
              await client.call('bank.reopenReconciliation', { reconciliationId: last.id, reason });
            } catch (e) {
              throw new Error(errorText(e), { cause: e });
            }
            toast('Reopened: its lines are unlocked. Start a new reconciliation to finish it again.');
            await onChanged();
          }}
        >
          {reopenOutcome(dayText(last.through))}
        </ConfirmDialog>
      )}
    </div>
  );
}

// ── a deposit of cash and cheques (gap #11) ──

interface Waiting {
  id: string;
  customerName: string;
  method: 'cash' | 'cheque' | 'card' | 'other';
  amountCents: number;
  receivedAt: string;
  reference: string | null;
}

const METHOD_WORD = { cash: 'Cash', cheque: 'Cheque', card: 'Card', other: 'Other' } as const;

/**
 * Cash and cheques taken in sit in undeposited funds until they reach the bank: tick the ones on the
 * deposit slip, say when and where they went in, and deposits.record moves them, behind its confirm.
 */
function Deposit({
  bankAccountId,
  bankName,
  timeZone,
  toast,
  onDone,
}: {
  bankAccountId: string;
  bankName: string;
  timeZone?: string;
  toast: (m: string) => void;
  /** Deposited: how much went in, for the page to read its book balance until the books have it. */
  onDone: (depositedCents: number) => Promise<void>;
}) {
  const waiting = useQuery<{ payments: Waiting[]; totalCents: number }>('payments.undeposited', {
    kind: 'cash_cheque',
  });
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [depositedOn, setDepositedOn] = useState(todayIn(timeZone));
  const [reference, setReference] = useState('');
  const [confirming, setConfirming] = useState(false);
  // The deposit's id is held while the same slip is on screen, so a retry deposits it once.
  const save = useSaveKey(JSON.stringify([bankAccountId, depositedOn, [...skipped].sort()]));
  if (!waiting.data || waiting.data.payments.length === 0) return null;
  const picked = waiting.data.payments.filter((p) => !skipped.has(p.id));
  const total = picked.reduce((t, p) => t + p.amountCents, 0);
  const flip = (id: string) =>
    setSkipped((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const record = async () => {
    try {
      await client.call('deposits.record', {
        depositId: save.key,
        bankAccountId,
        kind: 'cash_cheque',
        depositedOn,
        paymentIds: picked.map((p) => p.id),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      });
    } catch (e) {
      throw new Error(errorText(e), { cause: e });
    }
    setSkipped(new Set());
    setReference('');
    save.renew();
    toast(`Deposited ${money(total)} to ${bankName}.`);
    await Promise.all([waiting.reload(), onDone(total)]);
  };
  return (
    <Section title="Cash and cheques to deposit">
      <form
        className="bp-bank-deposit"
        aria-label="A deposit"
        onSubmit={(e) => {
          e.preventDefault();
          if (picked.length && depositedOn) setConfirming(true);
        }}
      >
        <div className="bp-bank-slip" role="group" aria-labelledby="bp-bank-slip-h">
          <span className="bp-bank-label" id="bp-bank-slip-h">
            On the deposit slip
          </span>
          {waiting.data.payments.map((p) => (
            <div key={p.id} className="bp-bank-slip-row">
              <Checkbox isSelected={!skipped.has(p.id)} onChange={() => flip(p.id)}>
                {p.customerName}
                <span className="bp-bank-sub">
                  {METHOD_WORD[p.method]}
                  {p.reference ? ` ${p.reference}` : ''} · taken {dayText(dayIn(p.receivedAt, timeZone))}
                </span>
              </Checkbox>
              <span className="bp-bank-amount">{money(p.amountCents)}</span>
            </div>
          ))}
          <div className="bp-bank-slip-row" data-total="">
            <span className="bp-bank-sub">Waiting in all</span>
            <span className="bp-bank-amount">{money(waiting.data.totalCents)}</span>
          </div>
        </div>
        <div className="bp-bank-form">
          <TextField type="date" label="Deposited on" value={depositedOn} onChange={setDepositedOn} />
          <TextField
            label="Deposit slip number"
            value={reference}
            onChange={setReference}
            placeholder="Optional"
          />
        </div>
        <div className="bp-bank-acts">
          <Button variant="primary" type="submit" isDisabled={!picked.length || !depositedOn}>
            {`Deposit ${money(total)}`}
          </Button>
        </div>
      </form>
      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        title="Record the deposit"
        confirmLabel={`Record ${money(total)}`}
        cancelLabel="Cancel"
        onConfirm={record}
      >
        {picked.length} payment{picked.length === 1 ? '' : 's'}, {money(total)} in all, went into {bankName}{' '}
        on {dayText(depositedOn)}. They leave undeposited funds, and the bank line can be matched to this
        deposit.
      </ConfirmDialog>
    </Section>
  );
}

// ── a statement from the bank's site (gap #11) ──

const ACCEPT = '.csv,.ofx,.qfx,text/csv';

/**
 * A CSV or OFX statement through bank.importStatement: lines already in are skipped by the server (the
 * bank's id, or date, amount and description). A CSV says which column is which first.
 */
function Statement({
  bankAccountId,
  bankName,
  toast,
  onDone,
}: {
  bankAccountId: string;
  bankName: string;
  toast: (m: string) => void;
  onDone: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<{ name: string; text: string; format: 'csv' | 'ofx' } | null>(null);
  /** Why the chosen file can't be brought in (a PDF, a spreadsheet), instead of its columns. */
  const [wrong, setWrong] = useState<string | null>(null);
  const [cols, setCols] = useState<string[]>([]);
  const [map, setMap] = useState<Columns | null>(null);
  const [split, setSplit] = useState(false);
  const shut = () => {
    setOpen(false);
    setFile(null);
    setWrong(null);
  };
  const act = useAct(toast, async () => {
    shut();
    await onDone();
  });
  const pick = async (f: File) => {
    const text = await f.text();
    const kind = statementKind(f.name, text);
    if ('wrong' in kind) {
      setFile(null);
      setWrong(kind.wrong);
      return;
    }
    setWrong(null);
    setFile({ name: f.name, text, format: kind.format });
    if (kind.format === 'csv') {
      const c = csvHeader(text);
      const g = guessColumns(c);
      setCols(c);
      setMap(g);
      setSplit(!g.amount && !!(g.debit || g.credit));
    }
  };
  const ok = !!file && (file.format === 'ofx' || csvReady(map, split));
  const run = () => {
    if (!file || !ok) return;
    void act
      .run<{ newCount: number; duplicateCount: number }>('bank.importStatement', {
        bankAccountId,
        format: file.format,
        fileName: file.name,
        content: file.text,
        ...(file.format === 'csv' && map ? { csvColumns: csvColumns(map, split) } : {}),
      })
      .then((r) => {
        if (r)
          toast(
            `${r.newCount} new line${r.newCount === 1 ? '' : 's'} from ${file.name}` +
              (r.duplicateCount ? `; ${r.duplicateCount} already in, skipped.` : '.'),
          );
      });
  };
  const column = (key: keyof Columns, label: string, optional = false) => (
    <SelectField
      label={label}
      value={map?.[key] ?? ''}
      onChange={(v) => setMap({ ...(map as Columns), [key]: v })}
      options={[
        { value: '', label: optional ? 'None' : 'Pick a column…' },
        ...cols.map((c) => ({ value: c, label: c })),
      ]}
    />
  );
  return (
    <Section title="Bring in a statement">
      {!open ? (
        <div className="bp-bank-rec">
          <p className="bp-bank-say">
            A CSV or OFX file from {bankName}’s website. Lines already in are skipped.
          </p>
          <div className="bp-bank-acts">
            <Link onPress={() => setOpen(true)}>Choose a file…</Link>
          </div>
        </div>
      ) : (
        <form
          className="bp-bank-form"
          aria-label="Bring in a statement"
          onSubmit={(e) => {
            e.preventDefault();
            run();
          }}
        >
          <div className="bp-bank-wide">
            <FileField
              label="Statement file (CSV or OFX)"
              accept={ACCEPT}
              error={wrong}
              isDisabled={act.busy}
              onChoose={(f) => void pick(f)}
            />
          </div>
          {file?.format === 'ofx' && (
            <p className="bp-bank-say bp-bank-wide">An OFX file names its own columns.</p>
          )}
          {file?.format === 'csv' && map && (
            <>
              {column('date', 'Date')}
              {column('description', 'Description')}
              <div className="bp-bank-wide">
                <Checkbox isSelected={split} onChange={setSplit}>
                  Money in and out are in two columns
                </Checkbox>
              </div>
              {split ? (
                <>
                  {column('debit', 'Money out')}
                  {column('credit', 'Money in')}
                </>
              ) : (
                column('amount', 'Amount')
              )}
              {column('reference', 'Reference', true)}
            </>
          )}
          <div className="bp-bank-acts bp-bank-wide">
            <Button variant="primary" type="submit" isDisabled={!ok || act.busy}>
              {act.busy ? 'Bringing it in…' : 'Bring it in'}
            </Button>
            <Button onPress={shut} isDisabled={act.busy}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Section>
  );
}

// ── what's matched, under it all ──

function matchedColumns(busy: boolean, unmatch: (l: BankLine) => void): DataColumn<BankLine>[] {
  return [
    { id: 'day', label: 'Date', rowHeader: true, cell: (l) => ({ text: dayText(l.postedOn) }) },
    { id: 'what', label: 'Description', cell: (l) => ({ text: l.description }) },
    { id: 'amount', label: 'Amount', align: 'end', cell: (l) => ({ text: money(l.amountCents) }) },
    {
      id: 'undo',
      label: 'Undo',
      kind: 'action',
      // a wrong match comes undone until the month's reconciliation is finished (gap #11)
      cell: (l) => ({
        action: {
          text: 'Unmatch',
          label: `Unmatch ${l.description}`,
          isDisabled: busy,
          onPress: () => unmatch(l),
        },
      }),
    },
  ];
}

export function BankingPage({
  nav,
  held,
  toast,
  go,
  timeZone,
}: {
  nav: NavProps;
  held: readonly string[];
  toast: (m: string) => void;
  /** Books setup, where a bank account is added. */
  go: (part: BooksPart) => void;
  timeZone?: string;
}) {
  // deposits move ServiceTitan's payments while the company mirrors it: taken and banked there (BL-088)
  const mirroring = useMirror();
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', {});
  const zones = useQuery<{ zones: { components: { code: string }[] }[] }>('tax.listZones', {});
  // Recoverable tax on a purchase is GST or HST, as on a bill; BC PST is part of the price.
  const taxCode =
    (zones.data?.zones ?? [])
      .flatMap((z) => z.components.map((c) => c.code))
      .find((c) => c === 'HST' || c === 'GST') ?? 'GST';
  const [chosen, setChosen] = useState<string | null>(null);
  const bank = banks.data?.bankAccounts.find((b) => b.id === chosen) ?? banks.data?.bankAccounts[0];
  const open = useQuery<{ lines: BankLine[] }>(
    'bank.listLines',
    bank ? { bankAccountId: bank.id, status: 'unmatched' } : null,
  );
  const done = useQuery<{ lines: BankLine[] }>(
    'bank.listLines',
    bank ? { bankAccountId: bank.id, status: 'matched' } : null,
  );
  const reload = () => Promise.all([open.reload(), done.reload(), banks.reload()]);
  const act = useAct(toast, async () => {
    await reload();
  });
  // A bank line is never categorised to a control account: a customer's or vendor's money is a
  // payment matched to the line, and the server refuses those accounts too. Nor to its own bank.
  const ownBank = bank?.accountId;
  const postable = useMemo(
    () => (accounts.data?.accounts ?? []).filter((a) => a.active && !isControl(a) && a.id !== ownBank),
    [accounts.data, ownBank],
  );
  const crumb = usePageCrumb(nav);
  const head = { ...(crumb ? { crumb } : {}), title: PART.bp ?? PART.rail, lede: PART.lede };
  const frame = (body: ReactNode) => (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        {body}
      </main>
    </div>
  );

  if (banks.data && !bank)
    return frame(
      <ListPage
        head={head}
        list={{
          title: 'To review',
          children: (
            <p className="bp-bank-say">
              No bank account is set up yet. <Link onPress={() => go('setup')}>Add one in Books setup.</Link>
            </p>
          ),
        }}
      />,
    );
  const lines = open.data?.lines;
  return frame(
    <ListPage
      head={{
        ...head,
        ...(bank
          ? {
              figures: (
                <Figures
                  items={[
                    { label: `${bank.name}, book balance`, value: money(bank.bookBalanceCents) },
                    { label: 'Last reconciled balance', value: money(bank.reconciledBalanceCents) },
                    {
                      label: 'To review',
                      value: lines ? String(lines.length) : '…',
                      sub: lines?.length === 1 ? 'line' : 'lines',
                    },
                  ]}
                />
              ),
            }
          : {}),
      }}
      {...(banks.data && banks.data.bankAccounts.length > 1 && bank
        ? {
            filters: (
              <Segmented
                label="Bank account"
                items={banks.data.bankAccounts.map((b) => ({ id: b.id, label: b.name }))}
                selectedKey={bank.id}
                onSelectionChange={setChosen}
              />
            ),
          }
        : {})}
      list={{
        title: 'To review',
        ...(lines ? { count: lines.length } : {}),
        what: bank ? 'the bank lines' : 'the bank accounts',
        status: bank ? stateOf(lines, open.error) : banks.error ? 'failed' : 'loading',
        emptyText: 'Every line is matched.',
        failedText: (bank ? open.error : banks.error) ?? 'The bank lines didn’t load.',
        onRetry: () => void (bank ? open.reload() : banks.reload()),
        children:
          bank && lines ? (
            <BankingReview
              lines={lines}
              accounts={postable}
              taxCode={taxCode}
              act={act}
              toast={toast}
              reload={reload}
            />
          ) : null,
      }}
      {...(bank
        ? {
            aside: {
              title: 'Reconciliation',
              children: (
                <Reconcile
                  key={bank.id}
                  bank={bank}
                  toast={toast}
                  mayReopen={holdsAny(held, CLOSE_PERMS)}
                  onChanged={reload}
                />
              ),
              after: (
                <>
                  {mirroring ? (
                    <Section title="Cash and cheques to deposit">
                      <p className="bp-bank-say">Deposits: {MIRROR_LINE}</p>
                    </Section>
                  ) : (
                    <Deposit
                      key={bank.id}
                      bankAccountId={bank.id}
                      bankName={bank.name}
                      {...(timeZone ? { timeZone } : {})}
                      toast={toast}
                      onDone={async (deposited) => {
                        // the deposit posts to the books a moment after it's recorded (QA-716): the
                        // book balance is read until it has it, so the figures never keep the old one
                        const id = bank.id;
                        const want = bank.bookBalanceCents + deposited;
                        await Promise.all([
                          open.reload(),
                          readUntil(
                            () => client.call<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {}),
                            (r) => r.bankAccounts.find((b) => b.id === id)?.bookBalanceCents === want,
                          ).then(() => banks.reload()),
                        ]);
                      }}
                    />
                  )}
                  <Statement
                    key={`s${bank.id}`}
                    bankAccountId={bank.id}
                    bankName={bank.name}
                    toast={toast}
                    onDone={async () => {
                      await Promise.all([open.reload(), banks.reload()]);
                    }}
                  />
                </>
              ),
            },
            below: (
              // the table scrolls in its own box on a phone: its column takes no more than the page
              <div className="bp-bank-below">
                <Section title="Matched">
                  <DataTable
                    columns={matchedColumns(
                      act.busy,
                      (l) =>
                        void act.run(
                          'bank.unmatch',
                          { bankTransactionId: l.id },
                          'Unmatched: it’s back in To review.',
                        ),
                    )}
                    rows={done.data?.lines ?? []}
                    empty="Nothing matched yet."
                  />
                </Section>
              </div>
            ),
          }
        : {})}
    />,
  );
}
